import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Button,
  Empty,
  Input,
  Popconfirm,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useReactToPrint } from 'react-to-print';
import { nativeRequest, NativeApiError } from '../../NativeSession/transport';
import { openNativeSocket } from '../../NativeSession/socket';

type Kind = 'individual' | 'group';
type Prompt = { id: number; name: string; text: string };
type Report = {
  id: number;
  kind: Kind;
  prompt_id: number;
  prompt_name: string;
  player_id: number | null;
  player_ids: number[];
  group_report_name: string;
  provider: string;
  status: string;
  version: string;
  operation_id: string | null;
};
type Job = {
  id: string;
  status: string;
  error?: string;
  items: { id: number; status: string }[];
};
type Bootstrap = {
  session_id: number;
  run: { released_date: string } | null;
  context: { rendered_text: string; signature: string } | null;
  context_error?: string;
  players: { id: number; username: string; name: string }[];
  reports: Report[];
  jobs: Job[];
};
type Detail = {
  id: number;
  status: string;
  version: string;
  response: string;
  injected_context_text: string;
  provider: string;
  prompt_name: string;
};

const providers = [
  { value: 'anthropic', label: 'Anthropic (Claude Opus 4.7)' },
  { value: 'openai', label: 'OpenAI (GPT-5.5 Pro)' },
];
const active = (status: string) => status === 'pending' || status === 'running';

export async function promptSignature(prompt: Prompt, kind: Kind) {
  const bytes = new TextEncoder().encode(
    JSON.stringify([prompt.id, prompt.name, prompt.text, kind]),
  );
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), b =>
    b.toString(16).padStart(2, '0'),
  ).join('');
}

/** Session-keyed native state. No legacy report actions, reducers, or batch locks. */
export default function NativeAIRunner({
  sessionId,
  sessionLabel,
  kind,
  prompt,
  unsaved,
  onPrompt,
}: {
  sessionId: number;
  sessionLabel: string;
  kind: Kind;
  prompt?: Prompt;
  unsaved: boolean;
  onPrompt: (id: number) => void;
}) {
  const path = `/fast/sessions/${sessionId}/admin/ai/`;
  const [data, setData] = useState<Bootstrap | null>(null);
  const [error, setError] = useState('');
  const [connectionError, setConnectionError] = useState('');
  const [players, setPlayers] = useState<number[]>([]);
  const [name, setName] = useState('');
  const [provider, setProvider] = useState('anthropic');
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [detailError, setDetailError] = useState('');
  const [detailReload, setDetailReload] = useState(0);
  const generation = useRef(0);
  const alive = useRef(true);
  const pendingIntent = useRef<{ fingerprint: string; key: string }>();
  const printRef = useRef<HTMLDivElement>(null);

  const refresh = useCallback(async () => {
    const ticket = ++generation.current;
    const value = await nativeRequest<Bootstrap>(path);
    if (alive.current && ticket === generation.current && value) setData(value);
  }, [path]);

  useEffect(() => {
    alive.current = true;
    void refresh().catch(reason => {
      if (alive.current) setError(String(reason));
    });
    const socket = openNativeSocket<{ ai: Bootstrap }>(
      sessionId,
      'admin_ai',
      value => {
        ++generation.current;
        setData(value.ai);
      },
      (connected, reason) =>
        setConnectionError(
          reason || (connected ? '' : 'Reconnecting to report updates…'),
        ),
    );
    return () => {
      alive.current = false;
      ++generation.current;
      socket.close();
    };
  }, [sessionId, refresh]);

  useEffect(() => {
    setPlayers([]);
    setName('');
    setDetail(null);
    setError('');
  }, [kind]);

  const reports = data?.reports || [];
  const matching = reports.find(
    r =>
      r.kind === kind &&
      r.prompt_id === prompt?.id &&
      (kind === 'individual'
        ? players.length === 1 && r.player_id === players[0]
        : !!name.trim() && r.group_report_name === name.trim()),
  );
  const current =
    detail && detail.id === matching?.id && detail.version === matching.version
      ? detail
      : null;
  const complete = current?.status === 'complete';
  const overlap = reports.some(
    r =>
      r.kind === kind &&
      r.prompt_id === prompt?.id &&
      active(r.status) &&
      (kind === 'individual'
        ? players.includes(r.player_id as number)
        : r.group_report_name === name.trim()),
  );
  const commonProvider = useMemo(() => {
    if (kind === 'group')
      return matching?.status === 'complete' ? matching.provider : 'anthropic';
    if (!players.length) return 'anthropic';
    const existing = players.map(
      pid =>
        data?.reports.find(
          r =>
            r.kind === kind &&
            r.prompt_id === prompt?.id &&
            r.player_id === pid &&
            r.status === 'complete',
        )?.provider,
    );
    return existing.every(p => p && p === existing[0])
      ? existing[0]
      : 'anthropic';
  }, [
    data?.reports,
    kind,
    matching?.provider,
    matching?.status,
    players,
    prompt?.id,
  ]);
  useEffect(() => setProvider(commonProvider || 'anthropic'), [commonProvider]);

  useEffect(() => {
    const controller = new AbortController();
    setDetail(null);
    setDetailError('');
    if (matching?.status === 'complete') {
      void nativeRequest<Detail>(
        `${path}reports/${matching.id}/`,
        undefined,
        controller.signal,
      )
        .then(value => {
          if (!controller.signal.aborted) setDetail(value);
        })
        .catch(reason => {
          if (!controller.signal.aborted) setDetailError(String(reason));
        });
    }
    return () => controller.abort();
  }, [path, matching?.id, matching?.version, matching?.status, detailReload]);

  const action = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await work();
      if (alive.current) await refresh();
    } catch (reason) {
      if (alive.current) {
        setError(String(reason));
        if (reason instanceof NativeApiError && reason.status === 409) {
          pendingIntent.current = undefined;
          void refresh().catch(() => undefined);
        }
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  const generate = () =>
    action(async () => {
      if (!prompt || !data?.context) return;
      const body = {
        kind,
        prompt_id: prompt.id,
        provider,
        player_ids: [...players].sort((a, b) => a - b),
        group_report_name: kind === 'group' ? name.trim() : '',
        context_signature: data.context.signature,
        prompt_signature: await promptSignature(prompt, kind),
      };
      const fingerprint = JSON.stringify(body);
      if (pendingIntent.current?.fingerprint !== fingerprint)
        pendingIntent.current = { fingerprint, key: crypto.randomUUID() };
      await nativeRequest(`${path}generate/`, {
        ...body,
        key: pendingIntent.current.key,
      });
      pendingIntent.current = undefined;
    });
  const print = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${current?.prompt_name || 'AI Report'} - ${kind === 'group' ? name : `player ${players[0]}`}`,
    pageStyle:
      '@page { size: auto; margin: 16mm; } body { -webkit-print-color-adjust: exact; }',
  });
  const failed =
    data?.jobs.filter(
      j =>
        j.status === 'failed' &&
        j.items.some(
          i =>
            i.status === 'failed' &&
            reports.some(r => r.id === i.id && r.operation_id === j.id),
        ),
    ) || [];

  return (
    <>
      {error && (
        <Alert
          type="error"
          title={error}
          action={<Button onClick={() => void action(refresh)}>Refresh</Button>}
        />
      )}
      {connectionError && <Alert type="info" title={connectionError} />}
      {!data ? (
        <Spin />
      ) : (
        <>
          <div>
            <Typography.Text strong>Automatic Session Context</Typography.Text>
            <Typography.Paragraph
              type="secondary"
              style={{ marginTop: 4, marginBottom: 8 }}
            >
              {complete
                ? 'Snapshot used for the selected report.'
                : 'Server-generated context that will be sent with the prompt.'}{' '}
              Put {'{{SESSION_CONTEXT}}'} in Prompt Text to control its
              position; otherwise it is appended automatically.
            </Typography.Paragraph>
            <Input.TextArea
              readOnly
              aria-label="Automatic Session Context"
              autoSize={{ minRows: 4, maxRows: 10 }}
              value={
                (complete && current.injected_context_text) ||
                data.context?.rendered_text ||
                'Automatic session context is unavailable.'
              }
            />
            {data.context_error && (
              <Typography.Text type="danger">
                {data.context_error}
              </Typography.Text>
            )}
            {!data.run && (
              <Typography.Text>
                Start this session before generating reports.
              </Typography.Text>
            )}
          </div>
          {kind === 'group' && (
            <div>
              <Typography.Text strong>Reports</Typography.Text>
              <Select
                aria-label="Reports"
                allowClear
                style={{ width: '100%', marginTop: 8 }}
                value={matching?.id}
                placeholder="Select an existing report"
                options={reports
                  .filter(r => r.kind === 'group')
                  .map(r => ({
                    value: r.id,
                    label: `${r.group_report_name} | ${r.prompt_name}${r.status === 'complete' ? ' ✓' : ''}`,
                  }))}
                onChange={id => {
                  const row = reports.find(r => r.id === id);
                  if (row) {
                    onPrompt(row.prompt_id);
                    setName(row.group_report_name);
                    setPlayers(row.player_ids);
                  }
                }}
              />
            </div>
          )}
          <div>
            <Typography.Text strong>
              {kind === 'group' ? 'Players' : 'Player(s)'}
            </Typography.Text>
            <Select
              aria-label="Report players"
              mode="multiple"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Select player(s)"
              value={players}
              onChange={setPlayers}
              options={data.players.map(p => ({
                value: p.id,
                label:
                  p.username +
                  (kind === 'individual' &&
                  reports.some(
                    r =>
                      r.prompt_id === prompt?.id &&
                      r.player_id === p.id &&
                      r.status === 'complete',
                  )
                    ? ' ✓'
                    : ''),
              }))}
            />
          </div>
          {kind === 'group' && (
            <div>
              <Typography.Text strong>Group Report Name</Typography.Text>
              <Input
                aria-label="Group report name"
                value={name}
                maxLength={200}
                style={{ marginTop: 8 }}
                onChange={e => setName(e.target.value)}
              />
            </div>
          )}
          <div>
            <Typography.Text strong>Provider</Typography.Text>
            <Select
              aria-label="Report provider"
              value={provider}
              onChange={setProvider}
              options={providers}
              style={{ width: '100%', marginTop: 8 }}
            />
          </div>
          <Space wrap>
            <Button
              type="primary"
              onClick={generate}
              loading={busy || overlap}
              disabled={
                busy ||
                overlap ||
                !data.run ||
                !data.context ||
                !prompt ||
                unsaved ||
                !players.length ||
                (kind === 'group' && (players.length < 2 || !name.trim()))
              }
            >
              Generate
            </Button>
            <Button
              onClick={print}
              disabled={!complete}
              icon={<DownloadOutlined />}
            >
              Download PDF
            </Button>
            <Popconfirm
              title="Delete this AI response?"
              okText="Delete"
              cancelText="Cancel"
              onConfirm={() =>
                action(() =>
                  nativeRequest(`${path}reports/${matching?.id}/delete/`, {
                    version: matching?.version,
                  }),
                )
              }
            >
              <Button danger disabled={!complete || busy}>
                Delete Response
              </Button>
            </Popconfirm>
            {unsaved && (
              <Typography.Text type="warning">
                Save prompt changes before generating.
              </Typography.Text>
            )}
          </Space>
          {failed.map(job => (
            <Alert
              key={job.id}
              type="error"
              title={job.error || 'Some reports failed.'}
              action={
                <Button
                  disabled={busy}
                  onClick={() =>
                    action(() =>
                      nativeRequest(`${path}jobs/${job.id}/retry/`, {}),
                    )
                  }
                >
                  Retry failed reports
                </Button>
              }
            />
          ))}
          {data.jobs
            .filter(j => active(j.status))
            .map(job => (
              <Typography.Text key={job.id} type="secondary">
                Generating reports:{' '}
                {job.items.filter(i => i.status === 'complete').length} /{' '}
                {job.items.length} complete
              </Typography.Text>
            ))}
          <div
            style={{
              flex: 1,
              minHeight: 160,
              overflowY: 'auto',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              padding: 16,
              background: '#fff',
            }}
          >
            {detailError ? (
              <Alert
                type="error"
                title={detailError}
                action={
                  <Button onClick={() => setDetailReload(v => v + 1)}>
                    Retry loading
                  </Button>
                }
              />
            ) : complete ? (
              <div ref={printRef}>
                <Typography.Title level={3}>
                  {current.prompt_name}
                </Typography.Title>
                <Typography.Paragraph type="secondary">
                  Session: {sessionLabel}
                </Typography.Paragraph>
                {kind === 'group' && (
                  <Typography.Paragraph type="secondary">
                    Group report name: {matching?.group_report_name}
                  </Typography.Paragraph>
                )}
                <Typography.Paragraph type="secondary">
                  {kind === 'group' ? 'Players' : 'Player'}:{' '}
                  {data.players
                    .filter(p =>
                      (kind === 'group'
                        ? matching?.player_ids
                        : players
                      )?.includes(p.id),
                    )
                    .map(p => p.username)
                    .join(', ')}
                </Typography.Paragraph>
                <Typography.Paragraph type="secondary">
                  Provider:{' '}
                  {providers.find(p => p.value === current.provider)?.label ||
                    current.provider}
                </Typography.Paragraph>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {current.response}
                </ReactMarkdown>
              </div>
            ) : matching?.status === 'complete' ? (
              <Spin />
            ) : matching && active(matching.status) ? (
              <Typography.Text>Generating this report…</Typography.Text>
            ) : (
              <Empty
                description={
                  players.length > 1 && kind === 'individual'
                    ? 'Select a single player to preview a report. Generate runs a report for every selected player.'
                    : 'Select a prompt and player(s) to view or run a report'
                }
              />
            )}
          </div>
        </>
      )}
    </>
  );
}
